import { Loader } from '@jutro/components';
import { TranslatorContext } from '@jutro/locale';
import { ServiceManager } from '@jutro/legacy/services';
import { withAuthenticationContext } from '@xengage/gw-digital-auth-react';
import { readViewModelValue } from '@xengage/gw-jutro-adapters-react';
import { PhoneUtil } from '@xengage/gw-portals-viewmodel-js';
import {
    ViewModelForm,
    withViewModelService
} from '@xengage/gw-portals-viewmodel-react';
import { ContactService } from 'gw-capability-gateway';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState, useCallback } from 'react';
import messages from './ContactDetails.messages';
import metadata from './ContactDetails.metadata.json5';
import styles from './ContactDetails.module.scss';

import { Link } from '@jutro/router';

function ContactDetailsPage(props) {
    const { location, authHeader, viewModelService } =props;
    const [accountContactDetailsVM, setAccountContactDetailsVM] = useState({});
    const [showLoadingIndicator, setShowLoadingIndicator] = useState(true);
    const contactId = location.state.contactId;
    const accountOrPolicyNumber = location.state.accountOrPolicyNumber;
    const contact = location.state.contact;
    const relatedTo = location.state.relatedTo;

    const translator = useContext(TranslatorContext);

    useEffect(() => {
        getAccountContacts();
    }, []);

    const getAccountContacts = useCallback(async() => {
        let getContactDetailsPromise;

        if (contact !== 'policyContacts') {
            getContactDetailsPromise = await ContactService.getAccountContactDetails(contactId, authHeader);
        } else {
            getContactDetailsPromise = ContactService.getPolicyContactDetails(
                contactId,
                accountOrPolicyNumber,
                authHeader
            );
        }
        createContactVM(getContactDetailsPromise);
         setShowLoadingIndicator(false);
    },[]);

    const createContactVM = useCallback((response) =>{
        const contactRole = relatedTo === 'policies'
        ? 'edge.capabilities.gateway.contact.dto.PolicyContactDetailsDTO'
        : 'edge.capabilities.policycommon.accountcontact.dto.AccountContactDTO';  //AccountContactDetailsDTO wasn't getting generated in product-metadata
        const contactVM = viewModelService.create(response, 'pc', contactRole);
        
        setAccountContactDetailsVM(contactVM.value);
    },[viewModelService]);

    const getAssociatedAccountsIcon = () => {
        const accountsList =  accountContactDetailsVM.associatedAccounts;       

        const overrides =
            accountsList &&
            accountsList.map((account, index) => {
                return {
                    [`associatedAccounts${index}`]: {
                        to: `/accounts/${account.accountNumber}/summary`,
                        content: `${account.accountHolderName} (${account.accountNumber})`,
                    },
                    [`associatedAccountsIcon${index}`]: {
                        icon:
                            account.subtype === 'Company'
                                ? 'business'
                                : 'person',
                    },
                };
            });
        return Object.assign({}, ...overrides);
    };

    const getWorkPhoneNumber = workNumber => {
        if (_.isNil(workNumber)) {
            return null;
        }
        const localeService = ServiceManager.getService('locale-service');
        const phoneUtil = PhoneUtil();
        return phoneUtil.prettyPrint(
            workNumber,
            localeService.getDefaultCountryCode()
        );
    };

    if (showLoadingIndicator) {
        return <Loader loaded={!showLoadingIndicator} />;
    }

    const overrideProps = {
        '@field': {
            readOnly: true,
        },
        contactDetailsContainer: {
            visible: !showLoadingIndicator,
        },
        policySourceState: {
            visible: contact === 'policyContacts',
            to: {
                pathname: `/${relatedTo}/${accountOrPolicyNumber}/contacts`,
                state: {
                    selectTab: 'policyContactsTab',
                },
            },
        },
        accountSourceState: {
            visible: contact === 'accountContacts',
            to: {
                pathname: `/${relatedTo}/${accountOrPolicyNumber}/contacts`,
                state: {
                    selectTab: 'accountContactsTab',
                },
            },
        },
        associatedSourceState: {
            visible: contact === 'associatedContacts',
            to: {
                pathname: `/${relatedTo}/${accountOrPolicyNumber}/contacts`,
                state: {
                    selectTab: 'associatedContactsTab',
                },
            },
        },
        accountContactStatusContainer: {
            visible:
                relatedTo !== 'policies' &&
                !_.get(accountContactDetailsVM, 'active'),
        },
        contactDetailsCompanySection: {
            visible:
                _.get(accountContactDetailsVM, 'subtype') ===
                'Company',
        },
        contactDetailsPersonSection: {
            visible:
                _.get(accountContactDetailsVM, 'subtype') ===
                'Person',
        },
        companyWorkNumberContainer: {
            visible:
                _.get(accountContactDetailsVM, 'workNumber') !==
                undefined,
        },
        companyFaxContainer: {
            visible:
                _.get(accountContactDetailsVM, 'faxNumber') !==
                undefined,
        },
        companyEmailAddressContainer: {
            visible:
                _.get(accountContactDetailsVM, 'emailAddress1') !==
                undefined,
        },
        contactCompanyAddressContainer: {
            visible:
                _.get(
                    accountContactDetailsVM,
                    'primaryAddress.displayName'
                ) !== undefined,
        },
        personDateOfBirthContainer: {
            visible:
                _.get(accountContactDetailsVM, 'dateOfBirth') !==
                undefined,
        },
        personMaritalStatusContainer: {
            visible:
                _.get(accountContactDetailsVM, 'maritalStatus') !==
                undefined,
        },
        personHomeNumberContainer: {
            visible:
                _.get(accountContactDetailsVM, 'homeNumber') !==
                undefined,
        },
        personHomeNumberLabel: {
            content:
                _.get(accountContactDetailsVM, 'primaryPhoneType') ===
                'home'
                    ? translator(messages.homePhonePrimary)
                    : translator(messages.homePhone),
        },
        personWorkNumberContainer: {
            visible:
                _.get(accountContactDetailsVM, 'workNumber') !==
                undefined,
        },
        personWorkNumberLabel: {
            content:
                _.get(accountContactDetailsVM, 'primaryPhoneType') ===
                'work'
                    ? translator(messages.workPhonePrimary)
                    : translator(messages.workPhone),
        },
        personCellNumberContainer: {
            visible:
                _.get(accountContactDetailsVM, 'cellNumber') !==
                undefined,
        },
        personCellNumberLabel: {
            content:
                _.get(accountContactDetailsVM, 'primaryPhoneType') ===
                'mobile'
                    ? translator(messages.mobilePrimary)
                    : translator(messages.mobile),
        },
        personEmailAddressContainer: {
            visible:
                _.get(accountContactDetailsVM, 'emailAddress1') !==
                undefined,
        },
        contactPersonAddressContainer: {
            visible:
                _.get(
                    accountContactDetailsVM,
                    'primaryAddress.displayName'
                ) !== undefined,
        },
        contactRoles: {
            roles: _.get(
                accountContactDetailsVM,
                contact === 'policyContacts'
                    ? 'policyContactRoles'
                    : 'accountContactRoles'
            ),
            associatedContacts: contact,
            relatedRoles:
                contact === 'policyContacts' ? 'policy' : 'account',
            contactType: _.get(accountContactDetailsVM, 'subtype'),
        },
        companyWorkNumber: {
            value: getWorkPhoneNumber(
                _.get(accountContactDetailsVM, 'workNumber')
            ),
        },
        contactDisplayName: {
            content: _.get(accountContactDetailsVM, 'displayName'),
        },
        ...getAssociatedAccountsIcon(),
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveComponentMap: {
            link: Link,
        },
    };

    const readValue = (id, path) => {
        return readViewModelValue(
            metadata.pageContent,
            accountContactDetailsVM,
            id,
            path,
            overrideProps
        );
    };

    return (
        <ViewModelForm
            uiProps={metadata.pageContent}
            model={accountContactDetailsVM}
            overrideProps={overrideProps}
            resolveValue={readValue}
            componentMap={resolvers.resolveComponentMap}
            classNameMap={resolvers.resolveClassNameMap}
        />
    );
}

ContactDetailsPage.propTypes = {
    location: PropTypes.shape({
        state: PropTypes.shape({
            contact: PropTypes.string,
            accountOrPolicyNumber: PropTypes.string,
            relatedTo: PropTypes.string,
            contactId: PropTypes.string,
        }),
    }).isRequired,
    viewModelService: PropTypes.shape({
        create: PropTypes.func,
    }).isRequired,
    authHeader: PropTypes.shape({}),
};

export default withViewModelService(
    withAuthenticationContext(ContactDetailsPage)
);
